import Enums from "@/enums";

export type LanguageValueExisting = {
  languageId: number;
  content?: string;
  fieldTranslationId?: number | null;
  sectionControlLanguageId?: number | null;
  companyContentLanguageId?: number | null;
  humanEdited?: boolean;
};

export type LanguageValueLanguage = {
  id: number;
  name: string;
};

export type LanguageValueRow = {
  languageId: number;
  languageName: string;
  value: string;
  originalValue: string;
  fieldTranslationId?: number | null;
  sectionControlLanguageId?: number | null;
  companyContentLanguageId?: number | null;
  humanEdited: boolean;
  englishLocked: boolean;
};

export type LanguageValuesRoutingRow = {
  contentType: string;
  contentFormat?: number | string | null;
  supportsInlineEdit?: boolean;
  languages?: Array<{ languageId: number; content?: string }>;
};

export function buildLanguageValueRows(args: {
  languages: LanguageValueLanguage[];
  existing: LanguageValueExisting[];
  isSystemAdmin: boolean;
  canEditEnglish: boolean;
}): LanguageValueRow[] {
  const existingById = new Map(
    args.existing.map((entry) => [Number(entry.languageId), entry] as const)
  );

  const languageIds = new Set<number>();
  for (const lang of args.languages) {
    const id = Number(lang.id);
    if (Number.isFinite(id) && id > 0) languageIds.add(id);
  }
  for (const entry of args.existing) {
    const id = Number(entry.languageId);
    if (id === 1) languageIds.add(1);
  }

  const nameById = new Map(
    args.languages.map((lang) => [Number(lang.id), String(lang.name || "")] as const)
  );

  return [...languageIds]
    .sort((a, b) => a - b)
    .map((languageId) => {
      const existing = existingById.get(languageId);
      const value = String(existing?.content ?? "");
      return {
        languageId,
        languageName: nameById.get(languageId) || `Language ${languageId}`,
        value,
        originalValue: value,
        fieldTranslationId: existing?.fieldTranslationId ?? null,
        sectionControlLanguageId: existing?.sectionControlLanguageId ?? null,
        companyContentLanguageId: existing?.companyContentLanguageId ?? null,
        humanEdited: Boolean(existing?.humanEdited),
        englishLocked: languageId === 1 && !args.canEditEnglish,
      };
    });
}

export function isLanguageValueRowDirty(row: LanguageValueRow): boolean {
  return row.value !== row.originalValue;
}

export function canTranslateLanguageValueRow(row: LanguageValueRow, englishValue: string): boolean {
  return row.languageId > 1 && englishValue.trim().length > 0 && !row.humanEdited;
}

/** Plain-text types that use LanguageValuesEditModal from the Translations catalog. */
export function opensLanguageValuesModal(row: LanguageValuesRoutingRow): boolean {
  const type = String(row.contentType ?? "");
  if (type === "Field" || type === "SectionControl") return true;
  if (type !== "Company") return false;

  const format = row.contentFormat;
  if (
    format === Enums.CONTENT_FORMAT.Text
    || format === 0
    || format === "0"
    || format === "Text"
  ) {
    return true;
  }
  if (
    format === Enums.CONTENT_FORMAT.HTML
    || format === 1
    || format === "1"
    || format === "HTML"
  ) {
    return false;
  }
  if (
    format === Enums.CONTENT_FORMAT.JSON
    || format === 2
    || format === "2"
    || format === "JSON"
  ) {
    return false;
  }
  if (typeof format === "number") {
    return format === Enums.CONTENT_FORMAT.Text;
  }
  return Boolean(row.supportsInlineEdit);
}
